/**
 * THE WORDS OF THE FRAME — the menu, the rail and the groups.
 *
 * English is the origin and pt-BR is the translation, same as every other
 * dictionary in the app (`shared/i18n` seed). It lives beside `nav-items.ts`
 * because the frame is not a feature: it belongs to no `features/<x>/`, and
 * `shared/i18n` carries only what crosses everything.
 *
 * `nav-items.ts` holds KEYS from here and nothing else. That is the whole point
 * of the split: the table decides which screens exist and in what order, this
 * file decides how they are called, and the component decides when to resolve —
 * which is the only moment the active language is known.
 */
export const navMessages = {
	en: {
		groups: {
			trabalho: "Work",
			ia: "AI",
			construir: "Build",
			workspace: "Workspace",
			dev: "Dev",
			funis: "Funnels",
		},
		nav: {
			main: "Main",
			seeAll: "See all",
			/** `{n} waiting in {group}` — the dot the narrow rail draws instead of a badge. */
			waitingIn: "{n} in {group}",
		},
		rail: {
			pinOpen: "Pin the menu open",
			unpin: "Unpin the menu: it becomes a strip again and opens on hover",
			pinLabel: "Pin menu open",
			unpinLabel: "Unpin menu",
		},
		items: {
			home: { label: "Start", hint: "what to do now", labelDone: "Today" },
			inbox: { label: "Conversations", hint: "what customers wrote", terms: "tags labels" },
			missions: { label: "Missions", hint: "what the agents do on their own" },
			help: { label: "Help requests", hint: "questions from agents to a person" },
			contacts: { label: "Contacts", hint: "people", terms: "tags labels" },
			agents: { label: "Agents", hint: "build, run, serve" },
			suggestions: { label: "Suggestions", hint: "what the assistant offers on each screen" },
			site: { label: "Site", hint: "the business on the web" },
			funnels: { label: "Funnels", hint: "open the kanban, create, archive" },
			structure: { label: "Structure", hint: "screens and fields" },
			system: { label: "Your system", hint: "what you have, what you could have, every change" },
			automation: {
				label: "Automations",
				hint: "what the system does on its own — and your screens' buttons",
			},
			reports: { label: "Reports", hint: "your numbers, each on its own page — ask Ben for one" },
			memory: { label: "Memory", hint: "what the agents know" },
			channels: { label: "Channels", hint: "whatsapp, e-mail, site chat" },
			files: { label: "Files", hint: "what the agents wrote down" },
			settings: { label: "Settings", hint: "everything you adjust here" },
			manual: { label: "Manual", hint: "how the panel works" },
			billing: { label: "Billing", hint: "plan, credits, invoices" },
			platform: { label: "Platform", hint: "workspaces, log" },
			prompts: { label: "System instructions", hint: "what each role receives" },
			models: { label: "Models", hint: "which AI answers, and in what order" },
			voices: { label: "Voices", hint: "who speaks, and the voice of each agent sex" },
			toolCosts: { label: "Tool prices", hint: "what each tool call costs in credits" },
			platformUsage: { label: "AI usage", hint: "what the models cost, call by call" },
			platformBilling: { label: "Installation billing", hint: "what comes in, and from whom" },
			catalog: { label: "Catalog", hint: "plans and packs on sale" },
			showcase: { label: "Showcase", hint: "what a new workspace is offered" },
			partners: { label: "Partners", hint: "who refers, and how much they earned" },
		},
	},
	"pt-BR": {
		groups: {
			trabalho: "Trabalho",
			ia: "IA",
			construir: "Construir",
			workspace: "Workspace",
			dev: "Dev",
			funis: "Funis",
		},
		nav: {
			main: "Principal",
			seeAll: "Ver todos",
			waitingIn: "{n} em {group}",
		},
		rail: {
			pinOpen: "Fixar o menu aberto",
			unpin: "Soltar o menu: ele volta a ser uma faixa e abre ao receber o ponteiro",
			pinLabel: "Fixar menu aberto",
			unpinLabel: "Desafixar menu",
		},
		items: {
			home: { label: "Começar", hint: "o que fazer agora", labelDone: "Hoje" },
			inbox: { label: "Conversas", hint: "o que os clientes escreveram", terms: "etiquetas" },
			missions: { label: "Missões", hint: "o que os agentes fazem sozinhos" },
			help: { label: "Pedidos de ajuda", hint: "perguntas dos agentes a uma pessoa" },
			contacts: { label: "Contatos", hint: "pessoas", terms: "etiquetas" },
			agents: { label: "Assistentes", hint: "atendentes e o assistente do sistema" },
			suggestions: { label: "Sugestões", hint: "o que o assistente oferece em cada tela" },
			site: { label: "Site", hint: "o negócio na internet" },
			funnels: { label: "Funis", hint: "abrir o kanban, criar, arquivar" },
			structure: { label: "Estrutura", hint: "telas e campos" },
			system: { label: "Seu sistema", hint: "o que você tem, o que pode ter, cada mudança" },
			automation: {
				label: "Automações",
				hint: "o que o sistema faz sozinho — e os botões das telas",
			},
			reports: {
				label: "Relatórios",
				hint: "seus números, cada um com sua página — peça um ao Ben",
			},
			memory: { label: "Memória", hint: "o que os agentes sabem" },
			channels: { label: "Canais", hint: "whatsapp, e-mail, chat do site" },
			files: { label: "Arquivos", hint: "o que os agentes anotaram" },
			settings: { label: "Configurações", hint: "tudo que se ajusta aqui" },
			manual: { label: "Manual", hint: "como o painel funciona" },
			billing: { label: "Cobrança", hint: "plano, créditos, faturas" },
			platform: { label: "Plataforma", hint: "workspaces, log" },
			prompts: { label: "Instruções do sistema", hint: "o que cada função recebe" },
			models: { label: "Modelos", hint: "qual IA responde, e em que ordem" },
			voices: { label: "Vozes", hint: "quem fala, e a voz de cada sexo de agente" },
			toolCosts: {
				label: "Preços de ferramenta",
				hint: "o que cada chamada de ferramenta custa em créditos",
			},
			platformUsage: {
				label: "Consumo de IA",
				hint: "o que os modelos custaram, chamada a chamada",
			},
			platformBilling: { label: "Cobrança da instalação", hint: "quanto entra, e de quem" },
			catalog: { label: "Catálogo", hint: "planos e pacotes à venda" },
			showcase: { label: "Vitrine", hint: "o que se oferece a um workspace novo" },
			partners: { label: "Parceiros", hint: "quem indica, e quanto ganhou" },
		},
	},
	el: {
		groups: {
			trabalho: "Εργασία",
			ia: "AI",
			construir: "Κατασκευή",
			workspace: "Χώρος εργασίας",
			dev: "Dev",
			funis: "Χοάνες",
		},
		nav: {
			main: "Κύριο",
			seeAll: "Προβολή όλων",
			waitingIn: "{n} σε {group}",
		},
		rail: {
			pinOpen: "Καρφίτσωμα του μενού ανοιχτού",
			unpin: "Ξεκαρφίτσωμα του μενού: γίνεται πάλι λωρίδα και ανοίγει με τον δείκτη",
			pinLabel: "Καρφίτσωμα μενού ανοιχτού",
			unpinLabel: "Ξεκαρφίτσωμα μενού",
		},
		items: {
			home: { label: "Έναρξη", hint: "τι να κάνεις τώρα", labelDone: "Σήμερα" },
			inbox: { label: "Συνομιλίες", hint: "τι έγραψαν οι πελάτες", terms: "ετικέτες" },
			missions: { label: "Αποστολές", hint: "τι κάνουν οι πράκτορες μόνοι τους" },
			help: { label: "Αιτήματα βοήθειας", hint: "ερωτήσεις των πρακτόρων προς ένα άτομο" },
			contacts: { label: "Επαφές", hint: "άτομα", terms: "ετικέτες" },
			agents: { label: "Πράκτορες", hint: "κατασκευή, χειρισμός, εξυπηρέτηση" },
			suggestions: { label: "Προτάσεις", hint: "τι προτείνει ο βοηθός σε κάθε οθόνη" },
			site: { label: "Ιστότοπος", hint: "η επιχείρηση στο διαδίκτυο" },
			funnels: { label: "Χοάνες", hint: "άνοιγμα kanban, δημιουργία, αρχειοθέτηση" },
			structure: { label: "Δομή", hint: "οθόνες και πεδία" },
			system: { label: "Το σύστημά σας", hint: "τι έχετε, τι μπορεί να έχει, κάθε αλλαγή" },
			automation: {
				label: "Αυτοματισμοί",
				hint: "τι κάνει μόνο του το σύστημα — και τα κουμπιά των οθονών",
			},
			reports: {
				label: "Αναφορές",
				hint: "οι αριθμοί σου, καθεμιά με δική της σελίδα — ζήτησέ τη από τον Ben",
			},
			memory: { label: "Μνήμη", hint: "τι ξέρουν οι πράκτορες" },
			channels: { label: "Κανάλια", hint: "whatsapp, email, chat ιστότοπου" },
			files: { label: "Αρχεία", hint: "τι σημείωσαν οι πράκτορες" },
			settings: { label: "Ρυθμίσεις", hint: "όλα όσα ρυθμίζεις εδώ" },
			manual: { label: "Εγχειρίδιο", hint: "πώς λειτουργεί η εφαρμογή" },
			billing: { label: "Χρεώσεις", hint: "πλάνο, πιστώσεις, τιμολόγια" },
			platform: { label: "Πλατφόρμα", hint: "χώροι εργασίας, αρχείο καταγραφής" },
			prompts: { label: "Οδηγίες συστήματος", hint: "τι λαμβάνει κάθε ρόλος" },
			models: { label: "Μοντέλα", hint: "ποιο AI απαντά, και με ποια σειρά" },
			voices: { label: "Φωνές", hint: "ποιος μιλάει, και η φωνή κάθε φύλου πράκτορα" },
			toolCosts: {
				label: "Τιμές εργαλείων",
				hint: "τι κοστίζει κάθε κλήση εργαλείου σε μονάδες",
			},
			platformUsage: { label: "Κατανάλωση AI", hint: "τι κόστισαν τα μοντέλα, κλήση προς κλήση" },
			platformBilling: { label: "Χρεώσεις εγκατάστασης", hint: "τι μπαίνει, και από ποιον" },
			catalog: { label: "Κατάλογος", hint: "πλάνα και πακέτα προς πώληση" },
			showcase: { label: "Βιτρίνα", hint: "τι προσφέρεται σε έναν νέο χώρο εργασίας" },
			partners: { label: "Συνεργάτες", hint: "ποιος παραπέμπει, και πόσα κέρδισε" },
		},
	},
	es: {
		groups: {
			trabalho: "Trabajo",
			ia: "IA",
			construir: "Construir",
			workspace: "Espacio de trabajo",
			dev: "Dev",
			funis: "Embudos",
		},
		nav: {
			main: "Principal",
			seeAll: "Ver todos",
			waitingIn: "{n} en {group}",
		},
		rail: {
			pinOpen: "Fijar el menú abierto",
			unpin: "Soltar el menú: vuelve a ser una franja y se abre al pasar el puntero",
			pinLabel: "Fijar menú abierto",
			unpinLabel: "Soltar menú",
		},
		items: {
			home: { label: "Empezar", hint: "qué hacer ahora", labelDone: "Hoy" },
			inbox: {
				label: "Conversaciones",
				hint: "lo que escribieron los clientes",
				terms: "etiquetas",
			},
			missions: { label: "Misiones", hint: "lo que los agentes hacen solos" },
			help: { label: "Solicitudes de ayuda", hint: "preguntas de los agentes a una persona" },
			contacts: { label: "Contactos", hint: "personas", terms: "etiquetas" },
			agents: { label: "Agentes", hint: "construir, operar, atender" },
			suggestions: { label: "Sugerencias", hint: "lo que el asistente ofrece en cada pantalla" },
			site: { label: "Web", hint: "el negocio en internet" },
			funnels: { label: "Embudos", hint: "abrir el kanban, crear, archivar" },
			structure: { label: "Estructura", hint: "pantallas y campos" },
			system: { label: "Tu sistema", hint: "lo que tienes, lo que puede tener, cada cambio" },
			automation: {
				label: "Automatizaciones",
				hint: "lo que el sistema hace solo — y los botones de tus pantallas",
			},
			reports: { label: "Informes", hint: "tus números, cada uno con su página — pídeselo a Ben" },
			memory: { label: "Memoria", hint: "lo que saben los agentes" },
			channels: { label: "Canales", hint: "whatsapp, correo, chat web" },
			files: { label: "Archivos", hint: "lo que los agentes anotaron" },
			settings: { label: "Ajustes", hint: "todo lo que se ajusta aquí" },
			manual: { label: "Manual", hint: "cómo funciona el panel" },
			billing: { label: "Facturación", hint: "plan, créditos, facturas" },
			platform: { label: "Plataforma", hint: "espacios de trabajo, historial" },
			prompts: { label: "Instrucciones del sistema", hint: "lo que recibe cada rol" },
			models: { label: "Modelos", hint: "qué IA responde, y en qué orden" },
			voices: { label: "Voces", hint: "quién habla, y la voz de cada sexo de agente" },
			toolCosts: {
				label: "Precios de herramientas",
				hint: "lo que cuesta cada llamada de herramienta en créditos",
			},
			platformUsage: {
				label: "Consumo de IA",
				hint: "lo que costaron los modelos, llamada a llamada",
			},
			platformBilling: { label: "Facturación de la instalación", hint: "lo que entra, y de quién" },
			catalog: { label: "Catálogo", hint: "planes y paquetes a la venta" },
			showcase: { label: "Escaparate", hint: "lo que se ofrece a un espacio nuevo" },
			partners: { label: "Socios", hint: "quién recomienda, y cuánto ganó" },
		},
	},
	fr: {
		groups: {
			trabalho: "Travail",
			ia: "IA",
			construir: "Construire",
			workspace: "Espace de travail",
			dev: "Dev",
			funis: "Pipelines",
		},
		nav: {
			main: "Principal",
			seeAll: "Tout voir",
			waitingIn: "{n} dans {group}",
		},
		rail: {
			pinOpen: "Épingler le menu ouvert",
			unpin: "Détacher le menu : il redevient une bande et s'ouvre au survol",
			pinLabel: "Épingler le menu ouvert",
			unpinLabel: "Détacher le menu",
		},
		items: {
			home: { label: "Démarrer", hint: "quoi faire maintenant", labelDone: "Aujourd'hui" },
			inbox: { label: "Conversations", hint: "ce que les clients ont écrit", terms: "étiquettes" },
			missions: { label: "Missions", hint: "ce que les agents font seuls" },
			help: { label: "Demandes d'aide", hint: "questions des agents à une personne" },
			contacts: { label: "Contacts", hint: "personnes", terms: "étiquettes" },
			agents: { label: "Agents", hint: "construire, piloter, répondre" },
			suggestions: { label: "Suggestions", hint: "ce que l'assistant propose sur chaque écran" },
			site: { label: "Site", hint: "l'entreprise sur le web" },
			funnels: { label: "Pipelines", hint: "ouvrir le kanban, créer, archiver" },
			structure: { label: "Structure", hint: "écrans et champs" },
			system: {
				label: "Votre système",
				hint: "ce que vous avez, ce qu’il peut avoir, chaque changement",
			},
			automation: {
				label: "Automatisations",
				hint: "ce que le système fait tout seul — et les boutons de vos écrans",
			},
			reports: {
				label: "Rapports",
				hint: "vos chiffres, chacun sur sa page — demandez-en un à Ben",
			},
			memory: { label: "Mémoire", hint: "ce que les agents savent" },
			channels: { label: "Canaux", hint: "whatsapp, e-mail, chat du site" },
			files: { label: "Fichiers", hint: "ce que les agents ont noté" },
			settings: { label: "Paramètres", hint: "tout ce qui se règle ici" },
			manual: { label: "Manuel", hint: "comment fonctionne le panneau" },
			billing: { label: "Facturation", hint: "forfait, crédits, factures" },
			platform: { label: "Plateforme", hint: "espaces de travail, journal" },
			prompts: { label: "Instructions système", hint: "ce que chaque rôle reçoit" },
			models: { label: "Modèles d'IA", hint: "quelle IA répond, et dans quel ordre" },
			voices: { label: "Voix", hint: "qui parle, et la voix de chaque sexe d'agent" },
			toolCosts: {
				label: "Prix des outils",
				hint: "ce que coûte chaque appel d'outil en crédits",
			},
			platformUsage: {
				label: "Consommation IA",
				hint: "ce que les modèles ont coûté, appel par appel",
			},
			platformBilling: {
				label: "Facturation de l'installation",
				hint: "ce qui rentre, et de qui",
			},
			catalog: { label: "Catalogue", hint: "forfaits et packs en vente" },
			showcase: { label: "Vitrine", hint: "ce qui est proposé à un nouvel espace" },
			partners: { label: "Partenaires", hint: "qui recommande, et combien il a gagné" },
		},
	},
	it: {
		groups: {
			trabalho: "Lavoro",
			ia: "IA",
			construir: "Costruisci",
			workspace: "Spazio di lavoro",
			dev: "Dev",
			funis: "Pipeline",
		},
		nav: {
			main: "Principale",
			seeAll: "Vedi tutti",
			waitingIn: "{n} in {group}",
		},
		rail: {
			pinOpen: "Blocca il menu aperto",
			unpin: "Sblocca il menu: torna a essere una striscia e si apre al passaggio del puntatore",
			pinLabel: "Blocca menu aperto",
			unpinLabel: "Sblocca menu",
		},
		items: {
			home: { label: "Inizia", hint: "cosa fare adesso", labelDone: "Oggi" },
			inbox: { label: "Conversazioni", hint: "cosa hanno scritto i clienti", terms: "etichette" },
			missions: { label: "Missioni", hint: "cosa fanno gli agenti da soli" },
			help: { label: "Richieste di aiuto", hint: "domande degli agenti a una persona" },
			contacts: { label: "Contatti", hint: "persone", terms: "etichette" },
			agents: { label: "Agenti", hint: "costruire, operare, rispondere" },
			suggestions: { label: "Suggerimenti", hint: "cosa propone l'assistente in ogni schermata" },
			site: { label: "Sito", hint: "l'attività sul web" },
			funnels: { label: "Pipeline", hint: "aprire il kanban, creare, archiviare" },
			structure: { label: "Struttura", hint: "schermate e campi" },
			system: {
				label: "Il tuo sistema",
				hint: "quello che hai, quello che può avere, ogni modifica",
			},
			automation: {
				label: "Automazioni",
				hint: "ciò che il sistema fa da solo — e i pulsanti delle tue schermate",
			},
			reports: {
				label: "Report",
				hint: "i tuoi numeri, ognuno con la sua pagina — chiedine uno a Ben",
			},
			memory: { label: "Memoria", hint: "cosa sanno gli agenti" },
			channels: { label: "Canali", hint: "whatsapp, e-mail, chat del sito" },
			files: { label: "File", hint: "cosa hanno annotato gli agenti" },
			settings: { label: "Impostazioni", hint: "tutto ciò che si regola qui" },
			manual: { label: "Manuale", hint: "come funziona il pannello" },
			billing: { label: "Fatturazione", hint: "piano, crediti, fatture" },
			platform: { label: "Piattaforma", hint: "spazi di lavoro, registro attività" },
			prompts: { label: "Istruzioni di sistema", hint: "cosa riceve ogni ruolo" },
			models: { label: "Modelli IA", hint: "quale IA risponde, e in che ordine" },
			voices: { label: "Voci", hint: "chi parla, e la voce di ogni sesso di agente" },
			toolCosts: {
				label: "Prezzi degli strumenti",
				hint: "quanto costa ogni chiamata di strumento in crediti",
			},
			platformUsage: {
				label: "Consumo di IA",
				hint: "quanto sono costati i modelli, chiamata per chiamata",
			},
			platformBilling: {
				label: "Fatturazione dell'installazione",
				hint: "cosa entra, e da chi",
			},
			catalog: { label: "Catalogo", hint: "piani e pacchetti in vendita" },
			showcase: { label: "Vetrina", hint: "ciò che si offre a uno spazio nuovo" },
			partners: { label: "Partner", hint: "chi segnala, e quanto ha guadagnato" },
		},
	},
	de: {
		groups: {
			trabalho: "Arbeit",
			ia: "KI",
			construir: "Bauen",
			workspace: "Workspace",
			dev: "Dev",
			funis: "Pipelines",
		},
		nav: {
			main: "Hauptmenü",
			seeAll: "Alle ansehen",
			waitingIn: "{n} in {group}",
		},
		rail: {
			pinOpen: "Menü offen anheften",
			unpin: "Menü lösen: Es wird wieder ein Streifen und öffnet sich beim Überfahren",
			pinLabel: "Menü offen anheften",
			unpinLabel: "Menü lösen",
		},
		items: {
			home: { label: "Start", hint: "was jetzt zu tun ist", labelDone: "Heute" },
			inbox: {
				label: "Konversationen",
				hint: "was die Kunden geschrieben haben",
				terms: "Etiketten Tags",
			},
			missions: { label: "Missionen", hint: "was die Agenten von allein tun" },
			help: { label: "Hilfeanfragen", hint: "Fragen der Agenten an eine Person" },
			contacts: { label: "Kontakte", hint: "Personen", terms: "Etiketten Tags" },
			agents: { label: "Agenten", hint: "bauen, betreiben, betreuen" },
			suggestions: { label: "Vorschläge", hint: "was der Assistent auf jedem Bildschirm anbietet" },
			site: { label: "Website", hint: "das Geschäft im Web" },
			funnels: { label: "Pipelines", hint: "Kanban öffnen, anlegen, archivieren" },
			structure: { label: "Struktur", hint: "Masken und Felder" },
			system: { label: "Ihr System", hint: "was Sie haben, was möglich ist, jede Änderung" },
			automation: {
				label: "Automatisierungen",
				hint: "was das System von selbst tut — und die Schaltflächen deiner Bildschirme",
			},
			reports: {
				label: "Berichte",
				hint: "Ihre Zahlen, jede auf eigener Seite — bitten Sie Ben darum",
			},
			memory: { label: "Gedächtnis", hint: "was die Agenten wissen" },
			channels: { label: "Kanäle", hint: "WhatsApp, E-Mail, Website-Chat" },
			files: { label: "Dateien", hint: "was die Agenten notiert haben" },
			settings: { label: "Einstellungen", hint: "alles, was hier eingestellt wird" },
			manual: { label: "Handbuch", hint: "wie das Panel funktioniert" },
			billing: { label: "Abrechnung", hint: "Tarif, Credits, Rechnungen" },
			platform: { label: "Plattform", hint: "Workspaces, Protokoll" },
			prompts: { label: "Systemanweisungen", hint: "was jede Rolle erhält" },
			models: { label: "Modelle", hint: "welche KI antwortet, und in welcher Reihenfolge" },
			voices: { label: "Stimmen", hint: "wer spricht, und die Stimme jedes Agentengeschlechts" },
			toolCosts: {
				label: "Werkzeugpreise",
				hint: "was jeder Werkzeugaufruf an Credits kostet",
			},
			platformUsage: {
				label: "KI-Verbrauch",
				hint: "was die Modelle gekostet haben, Aufruf für Aufruf",
			},
			platformBilling: {
				label: "Abrechnung der Installation",
				hint: "was hereinkommt, und von wem",
			},
			catalog: { label: "Katalog", hint: "Tarife und Pakete im Verkauf" },
			showcase: { label: "Schaufenster", hint: "was ein neuer Arbeitsbereich angeboten bekommt" },
			partners: { label: "Partner", hint: "wer empfiehlt, und wie viel er verdient hat" },
		},
	},
} as const;
